// Manuelle Bestellung anlegen — für Innendienst, wenn Kunde
// telefonisch / per E-Mail bestellt. Verwendet die Easybill-Artikel
// aus der Shop-Whitelist als auswählbare Positionen.
import { useState, useEffect } from 'react';
import { authFetch } from '../lib/auth-fetch';
import { X, Plus, Trash2, Save, Search } from 'lucide-react';
import { toast } from 'sonner';

const fmtEur = (v) => new Intl.NumberFormat('de-DE', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
}).format(Number(v || 0));

const emptyItem = () => ({
  articleId: null, articleNumber: '', title: '', quantity: 1,
  single_price_net: 0, vat_percent: 19, unit: 'Stück',
  description: '', groupName: '',
});

function ArticlePicker({ onPick, products }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = !q ? products : products.filter(p =>
    (p.title + ' ' + (p.articleNumber || '') + ' ' + (p.groupName || ''))
      .toLowerCase().includes(q.toLowerCase()),
  );
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-xs"
        data-testid="btn-article-picker"
      >
        <Search className="w-3 h-3" /> Artikel
      </button>
      {open && (
        <div className="absolute z-10 top-full right-0 mt-1 w-[450px] max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-lg" data-testid="article-picker-dropdown">
          <div className="p-2 border-b border-gray-100">
            <input autoFocus value={q} onChange={e => setQ(e.target.value)}
              placeholder="Suchen: Art-Nr., Titel oder Gruppe …"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#9ec043]" />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-xs text-gray-400 text-center">Keine Treffer</div>
            ) : filtered.slice(0, 80).map(p => (
              <button key={p.articleId} type="button"
                onClick={() => { onPick(p); setOpen(false); setQ(''); }}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                data-testid={`article-pick-${p.articleId}`}>
                <div className="text-xs font-medium text-[#32373c]">{p.title}</div>
                <div className="text-[10px] text-gray-400">
                  {p.articleNumber || '–'} · {p.groupName} · {fmtEur(p.single_price_net)} netto/{p.unit}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ManualOrderModal({ onClose, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  // 28.02.2026: Kunden-Auswahl-Modus. Default = B2B (bestehender Flow);
  // "eb-search" öffnet die Easybill-Volltextsuche wie bei Aufträgen,
  // damit auch sonstige EES-Kunden gewählt werden können.
  const [customerMode, setCustomerMode] = useState('b2b'); // 'b2b' | 'eb-search'
  const [customerId, setCustomerId] = useState('');
  const [ebQuery, setEbQuery] = useState('');
  const [ebResults, setEbResults] = useState([]);
  const [ebLoading, setEbLoading] = useState(false);
  const [customForm, setCustomForm] = useState({
    customerName: '', customerEmail: '',
    easybillCustomerId: '', salePriceLevel: '',
  });
  const [items, setItems] = useState([emptyItem()]);
  const [meta, setMeta] = useState({
    commission: '', customerNote: '',
    deliveryAddressMode: 'standard', deliveryAddress: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authFetch('/api/customers').then(r => r.ok && r.json().then(setCustomers));
  }, []);

  // 01.09.2026 (Bug-Fix): Produkte werden mit dem Preis-Level des
  // ausgewählten Kunden nachgeladen, damit der richtige Easybill-Preis
  // (sale_price2..10) in die Bestellung übernommen wird. Ohne Kunde =
  // Basispreis (`sale_price`).
  useEffect(() => {
    const lvl = customForm.salePriceLevel || '';
    const url = `/api/shop/products?scope=internal${lvl ? `&level=${encodeURIComponent(lvl)}` : ''}`;
    authFetch(url).then(async r => {
      if (!r.ok) return;
      const d = await r.json();
      const flat = [];
      for (const grp of (d.groups || [])) {
        for (const p of (grp.products || [])) flat.push({ ...p, groupName: grp.groupName });
      }
      setProducts(flat);
    });
  }, [customForm.salePriceLevel]);

  // Easybill-Kunden live suchen (mind. 2 Zeichen, 300ms Debounce).
  useEffect(() => {
    if (customerMode !== 'eb-search' || ebQuery.trim().length < 2) {
      setEbResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setEbLoading(true);
      const r = await authFetch(
        `/api/customers/easybill-search?q=${encodeURIComponent(ebQuery.trim())}`,
      );
      setEbLoading(false);
      if (r.ok) {
        const d = await r.json();
        setEbResults(d.items || []);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [customerMode, ebQuery]);

  const pickEbCustomer = (c) => {
    // Namens-Zusammenbau: Firma bevorzugt, sonst Vor-/Nachname
    const displayName = c.company || `${c.firstName} ${c.lastName}`.trim() || `Kunde ${c.number}`;
    setCustomerId('');
    setCustomForm({
      customerName: displayName,
      customerEmail: c.email || '',
      easybillCustomerId: String(c.id || ''),
      salePriceLevel: c.salePriceLevel || '',
    });
    setEbQuery(displayName);
    setEbResults([]);
    toast.success(`Kunde übernommen: ${displayName}`);
  };

  const selectedCustomer = customers.find(c => c.id === customerId);

  useEffect(() => {
    if (selectedCustomer) {
      setCustomForm({
        customerName: selectedCustomer.name || '',
        customerEmail: selectedCustomer.email || '',
        easybillCustomerId: selectedCustomer.easybillCustomerId || '',
        salePriceLevel: selectedCustomer.salePriceLevel || '',
      });
    }
  }, [selectedCustomer]);

  const updateItem = (i, patch) =>
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const removeItem = (i) =>
    setItems(prev => prev.filter((_, idx) => idx !== i));

  const pickArticleAt = (i) => (product) => updateItem(i, {
    articleId: product.articleId, articleNumber: product.articleNumber,
    title: product.title, description: product.description,
    groupName: product.groupName, single_price_net: product.single_price_net,
    vat_percent: product.vat_percent, unit: product.unit,
  });

  const total = items.reduce((s, it) =>
    s + (Number(it.quantity) || 0) * (Number(it.single_price_net) || 0), 0);

  const save = async () => {
    if (!customForm.customerName.trim()) { toast.error('Kundenname angeben'); return; }
    const validItems = items.filter(it => it.title.trim() && Number(it.quantity) > 0);
    if (validItems.length === 0) {
      toast.error('Mindestens eine Position mit Titel und Menge > 0');
      return;
    }
    if (meta.deliveryAddressMode === 'custom' && !meta.deliveryAddress.trim()) {
      toast.error('Bei abweichender Lieferadresse bitte Anschrift angeben.');
      return;
    }
    setSaving(true);
    const body = {
      customerId: customerId || null,
      customerName: customForm.customerName.trim(),
      customerEmail: customForm.customerEmail.trim() || null,
      easybillCustomerId: customForm.easybillCustomerId
        ? Number(customForm.easybillCustomerId) : null,
      salePriceLevel: customForm.salePriceLevel || null,
      items: validItems.map(it => ({
        ...it,
        quantity: Number(it.quantity),
        single_price_net: Number(it.single_price_net),
        vat_percent: Number(it.vat_percent),
      })),
      commission: meta.commission.trim() || null,
      customerNote: meta.customerNote.trim() || null,
      deliveryAddressMode: meta.deliveryAddressMode,
      deliveryAddress: meta.deliveryAddressMode === 'custom' ? meta.deliveryAddress.trim() : null,
    };
    const r = await authFetch('/api/shop/orders/manual', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setSaving(false);
    if (r.ok) {
      const d = await r.json();
      toast.success(`Bestellung #${d.id.slice(0,8)} angelegt`);
      onCreated?.();
      onClose();
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Speichern fehlgeschlagen');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-gray-200 w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="manual-order-modal">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#32373c]">Manuelle Bestellung anlegen (telefonisch / E-Mail)</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-4">
          {/* Kunde */}
          <div className="space-y-2">
            <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium">Kunde</label>
            {/* Auswahl-Modus */}
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-[11px]">
              <button type="button"
                onClick={() => { setCustomerMode('b2b'); setEbQuery(''); setEbResults([]); }}
                className={`px-3 py-1.5 ${customerMode === 'b2b'
                  ? 'bg-[#9ec043] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                data-testid="btn-mode-b2b">B2B-Kunde</button>
              <button type="button"
                onClick={() => { setCustomerMode('eb-search'); setCustomerId(''); }}
                className={`px-3 py-1.5 border-l border-gray-200 ${customerMode === 'eb-search'
                  ? 'bg-[#9ec043] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                data-testid="btn-mode-eb-search">EES-Kunde (Easybill-Suche)</button>
            </div>
            {customerMode === 'b2b' ? (
              <select value={customerId} onChange={e => setCustomerId(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white"
                data-testid="select-manual-customer">
                <option value="">— Manuell eintragen / nicht in B2B-Shop —</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.email}){c.salePriceLevel ? ' · ' + c.salePriceLevel : ''}
                  </option>
                ))}
              </select>
            ) : (
              <div className="relative">
                <div className="flex items-center gap-2">
                  <Search className="w-3.5 h-3.5 text-gray-400" />
                  <input value={ebQuery}
                    onChange={e => setEbQuery(e.target.value)}
                    placeholder="Firmenname / Kunden-Nr. / E-Mail suchen (min. 2 Zeichen)…"
                    className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs"
                    data-testid="input-eb-customer-search" />
                </div>
                {ebLoading && (
                  <div className="text-[10px] text-gray-400 mt-1">Suche in Easybill…</div>
                )}
                {ebResults.length > 0 && (
                  <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto"
                       data-testid="eb-customer-results">
                    {ebResults.map((c) => {
                      const displayName = c.company || `${c.firstName} ${c.lastName}`.trim() || `Kunde ${c.number}`;
                      return (
                        <button key={c.id} type="button"
                          onClick={() => pickEbCustomer(c)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                          data-testid={`eb-customer-pick-${c.id}`}>
                          <div className="text-xs font-medium text-[#32373c]">{displayName}</div>
                          <div className="text-[10px] text-gray-400">
                            Kunden-Nr. {c.number || '–'} · {c.email || 'ohne E-Mail'}
                            {c.salePriceLevel ? ' · ' + c.salePriceLevel : ''}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Kundenname *" value={customForm.customerName}
                onChange={e => setCustomForm(p => ({ ...p, customerName: e.target.value }))}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs" data-testid="input-manual-name" />
              <input placeholder="E-Mail (optional)" value={customForm.customerEmail}
                onChange={e => setCustomForm(p => ({ ...p, customerEmail: e.target.value }))}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs" data-testid="input-manual-email" />
              <input placeholder="Easybill-Kunden-ID" value={customForm.easybillCustomerId}
                onChange={e => setCustomForm(p => ({ ...p, easybillCustomerId: e.target.value }))}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs" data-testid="input-manual-eb-id" />
              <input placeholder="Preisgruppe (z.B. SALEPRICE2)" value={customForm.salePriceLevel}
                onChange={e => setCustomForm(p => ({ ...p, salePriceLevel: e.target.value }))}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs" data-testid="input-manual-pricelevel" />
            </div>
          </div>

          {/* Positionen */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium">
                Positionen {products.length > 0 && `(${products.length} Easybill-Artikel verfügbar)`}
              </label>
              <button onClick={() => setItems(prev => [...prev, emptyItem()])}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-gray-600 text-xs hover:bg-gray-200"
                data-testid="btn-add-manual-item">
                <Plus className="w-3 h-3" /> Position
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-2 space-y-2" data-testid={`manual-item-${i}`}>
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <input placeholder="Art-Nr." value={it.articleNumber}
                      onChange={e => updateItem(i, { articleNumber: e.target.value })}
                      className="col-span-3 px-2 py-1 border border-gray-200 rounded text-xs font-mono" />
                    <input placeholder="Bezeichnung *" value={it.title}
                      onChange={e => updateItem(i, { title: e.target.value })}
                      className="col-span-6 px-2 py-1 border border-gray-200 rounded text-xs" />
                    <div className="col-span-2">
                      <ArticlePicker products={products} onPick={pickArticleAt(i)} />
                    </div>
                    <button onClick={() => removeItem(i)} disabled={items.length === 1}
                      className="col-span-1 p-1 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded disabled:opacity-30">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-12 gap-2">
                    <input type="number" placeholder="Menge" value={it.quantity}
                      onChange={e => updateItem(i, { quantity: e.target.value })}
                      className="col-span-2 px-2 py-1 border border-gray-200 rounded text-xs tabular-nums" />
                    <input placeholder="Einheit" value={it.unit}
                      onChange={e => updateItem(i, { unit: e.target.value })}
                      className="col-span-2 px-2 py-1 border border-gray-200 rounded text-xs" />
                    <input type="number" step="0.01" placeholder="Einzelpreis netto" value={it.single_price_net}
                      onChange={e => updateItem(i, { single_price_net: e.target.value })}
                      className="col-span-3 px-2 py-1 border border-gray-200 rounded text-xs tabular-nums" />
                    <input type="number" step="0.1" placeholder="MwSt %" value={it.vat_percent}
                      onChange={e => updateItem(i, { vat_percent: e.target.value })}
                      className="col-span-2 px-2 py-1 border border-gray-200 rounded text-xs tabular-nums" />
                    <div className="col-span-3 text-right text-xs tabular-nums font-medium text-[#32373c] py-1">
                      {fmtEur((Number(it.quantity) || 0) * (Number(it.single_price_net) || 0))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-right text-sm font-bold mt-2 tabular-nums" data-testid="manual-total">
              Summe netto: {fmtEur(total)}
            </div>
          </div>

          {/* Meta */}
          <div className="space-y-2 bg-gray-50/50 rounded-lg p-3">
            <input placeholder="Kommission / Bauvorhaben" value={meta.commission}
              onChange={e => setMeta(p => ({ ...p, commission: e.target.value }))}
              className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs"
              data-testid="input-manual-commission" />
            <div>
              <div className="flex flex-wrap gap-3 mb-1 text-xs">
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" checked={meta.deliveryAddressMode === 'standard'}
                    onChange={() => setMeta(p => ({ ...p, deliveryAddressMode: 'standard' }))}
                    className="accent-[#9ec043]" />
                  <span>Standardadresse</span>
                </label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" checked={meta.deliveryAddressMode === 'custom'}
                    onChange={() => setMeta(p => ({ ...p, deliveryAddressMode: 'custom' }))}
                    className="accent-[#9ec043]" />
                  <span>Abweichende Lieferadresse</span>
                </label>
              </div>
              {meta.deliveryAddressMode === 'custom' && (
                <textarea value={meta.deliveryAddress}
                  onChange={e => setMeta(p => ({ ...p, deliveryAddress: e.target.value }))}
                  rows={2} placeholder="Firma\nStraße Nr.\nPLZ Ort"
                  className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs resize-none"
                  data-testid="input-manual-delivery" />
              )}
            </div>
            <textarea placeholder="Mitteilung / interne Notiz" value={meta.customerNote}
              onChange={e => setMeta(p => ({ ...p, customerNote: e.target.value }))}
              rows={2} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs resize-none"
              data-testid="input-manual-note" />
          </div>
        </div>
        <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:bg-gray-100">Abbrechen</button>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#9ec043] text-white text-xs font-medium hover:bg-[#7ca735] disabled:opacity-50"
            data-testid="btn-save-manual-order">
            <Save className="w-3.5 h-3.5" />{saving ? 'Speichere …' : 'Bestellung anlegen'}
          </button>
        </div>
      </div>
    </div>
  );
}
